import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import axios from "../api/axios";

export default function StaffApplicationFormPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  
  const branches = ['NCCU', 'NTU', 'TAIPEI'];
  
  const [availableRecruitments, setAvailableRecruitments] = useState([]);
  const [selectedRecruitment, setSelectedRecruitment] = useState(null);
  
  const fetchAvailableRecruitments = async () => {
    try {
      const response = await axios.get('/api/recruitment/public/active');
      setAvailableRecruitments(response.data);
    } catch (error) {
      // Silent fail
    }
  };
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAvailableRecruitments();
  }, []);

  // ✅ Fetch user profile data to prefill form
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.isLoggedIn) return;
      
      try {
        const profileResponse = await axios.get('/api/users/me');
        const profileData = profileResponse.data;
        
        // Prefill form with existing profile data
        setFormData(prev => ({
          ...prev,
          studentId: profileData.studentId || prev.studentId,
          major: profileData.major || prev.major,
          mbti: profileData.mbti || prev.mbti
        }));
      } catch (error) {
        // Silently fail - user can still fill the form manually
      }
    };
    
    fetchUserProfile();
  }, [user?.isLoggedIn]);
  
  const handleBranchChange = (branch) => {
    const recruitment = availableRecruitments.find(r => r.branch === branch && r.isCurrentlyOpen);
    if (recruitment) {
      setSelectedRecruitment(recruitment);
      setFormData(prev => ({
        ...prev,
        branch: branch,
        recruitmentPeriod: recruitment.semester
      }));
    } else {
      setSelectedRecruitment(null);
      setFormData(prev => ({
        ...prev,
        branch: branch
      }));
    }
  };

  const [formData, setFormData] = useState({
    name: user?.name || "",
    studentId: "",
    email: user?.email || "",
    major: "",
    year: "",
    lineId: "",
    instagramId: "",
    mbti: "",
    position1stPriority: "",
    position2ndPriority: "",
    position3rdPriority: "",
    motivation: "",
    developmentGoal: "",
    hasPortfolio: false,
    portfolioLink: "",
    hasRecruitmentBonus: null,
    recruitmentBonusLink: "",
    branch: "",
    recruitmentPeriod: "",
    agreesWithCommitment: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      
      // If 1st Priority is changed, clear 2nd and 3rd if they match
      if (name === 'position1stPriority') {
        if (prev.position2ndPriority === value) newData.position2ndPriority = '';
        if (prev.position3rdPriority === value) newData.position3rdPriority = '';
      }
      // If 2nd Priority is changed, clear 3rd if it matches 1st or 2nd
      else if (name === 'position2ndPriority') {
        if (prev.position3rdPriority === value || prev.position1stPriority === value) {
          newData.position3rdPriority = '';
        }
      }
      
      return newData;
    });
  };
  
  // Get available options for 2nd/3rd Priority based on what's already selected
  const getAvailableOptions = (priorityLevel) => {
    const allOptions = ['General Affairs', 'Public Relation', 'Event Planner'];
    const selected1st = formData.position1stPriority;
    const selected2nd = formData.position2ndPriority;
    
    if (priorityLevel === '2nd') {
      return allOptions.filter(opt => opt !== selected1st);
    }
    if (priorityLevel === '3rd') {
      return allOptions.filter(opt => opt !== selected1st && opt !== selected2nd);
    }
    return allOptions;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation - Check branch and recruitment period first
    if (!formData.branch || !formData.recruitmentPeriod) {
      setError("Please select a branch with an active recruitment period first.");
      return;
    }

    // Validate other required fields
    if (!formData.name || !formData.studentId || !formData.email || 
        !formData.major || !formData.instagramId || !formData.mbti ||
        !formData.position1stPriority || !formData.motivation || 
        !formData.developmentGoal || !formData.portfolioLink || 
        !formData.agreesWithCommitment) {
      setError("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post("/api/staff-application/submit", formData);
      alert("Application submitted successfully! We'll review your application and get back to you soon.");
      navigate("/");
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data || "Failed to submit application";
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if user is logged in
  if (!user || !user.isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Login Required</h1>
          <p className="text-gray-600 mb-6">Please log in to submit a staff application.</p>
          <button 
            onClick={() => navigate('/login')} 
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            🚀 SLAM Staff Application Form
          </h1>
          <p className="text-gray-600 text-center mb-8 italic text-base">
            ✨ Study Less, Achieve More.<br />
            We are the bridge connecting local Taiwanese and internationals in Taiwan.
          </p>
          <p className="text-gray-600 text-center mb-8 text-base">
          ✨ Join the Leadership Team! <br />
            Welcome to those who wish to join our dedicated SLAM Staff.
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded mt-8">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This form is for <span className="text-red-600 font-bold">STAFF</span> applications only. 
              <br></br><br></br>
              If you want to enjoy SLAM events as a member, Please apply for events on our <a href="/events" className="text-blue-600 hover:underline font-semibold">EVENTS PAGE</a> instead!
            </p>
          </div>
        </div>

        {/* Perks */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6 rounded-2xl mb-8">
          <h2 className="text-2xl font-bold mb-4">🎁 SLAM Staff Perks</h2>
          <ul className="space-y-2">
            <li>• Work in an international environment to improve your Chinese/English speaking skills</li>
            <li>• Enjoy staff benefits (Free SLAM Meet Entrance, Bonding Meal, Birthday Cake, After-Party Subsidy, and more!)</li>
          </ul>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
              {error}
            </div>
          )}

          {/* Section 0: Branch Selection */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500">
              I. Select Branch
            </h2>
            <div className="grid grid-cols-3 gap-4">
              {branches.map(branch => {
                const recruitment = availableRecruitments.find(r => r.branch === branch && r.isCurrentlyOpen);
                const isAvailable = !!recruitment;
                return (
                  <button
                    key={branch}
                    type="button"
                    onClick={() => handleBranchChange(branch)}
                    disabled={!isAvailable}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.branch === branch 
                        ? 'border-blue-600 bg-blue-50' 
                        : isAvailable 
                          ? 'border-gray-300 hover:border-blue-400 hover:bg-gray-50' 
                          : 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="font-bold text-lg mb-2">{branch}</div>
                    {isAvailable ? (
                      <div className="text-sm text-green-600 font-medium">✓ Open</div>
                    ) : (
                      <div className="text-sm text-gray-500">Not available</div>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedRecruitment && (
              <div className="mt-4 space-y-3">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm text-green-800">
                    <strong>Selected:</strong> {selectedRecruitment.branch} - {selectedRecruitment.semester}
                    <br />
                    <span>Period: {new Date(selectedRecruitment.startDate).toLocaleDateString()} ~ {new Date(selectedRecruitment.endDate).toLocaleDateString()}</span>
                  </p>
                </div>
                {/* Deadline emphasis */}
                <div className="bg-red-100 border-2 border-red-600 p-4 rounded-lg">
                  <p className="text-base font-bold text-red-800 text-center">
                    ⏰ Application Deadline: <span className="text-xl">{new Date(selectedRecruitment.endDate).toLocaleDateString()}</span>
                  </p>
                </div>
                {/* Learn More link for NCCU/NTU */}
                {(selectedRecruitment.branch === 'NCCU' || selectedRecruitment.branch === 'NTU') && (
                  <div className="bg-blue-50 border border-blue-300 p-4 rounded">
                    <p className="text-sm text-blue-900 text-center">
                      <a 
                        href="https://roan-sherbet-922.notion.site/Staff-Recruitment-Work-Less-Efficient-More-160dd27f765a46768b794265ba9a972e" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-700 hover:text-blue-900 hover:underline font-semibold"
                      >
                        📖 LEARN MORE ABOUT SLAM STAFF ROLES & RESPONSIBILITIES →
                      </a>
                    </p>
                  </div>
                )}
              </div>
            )}
            {availableRecruitments.length === 0 && (
              <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                <p className="text-sm text-yellow-800">
                  No active recruitment periods are currently available. Please contact admin or check back later.
                </p>
              </div>
            )}
          </section>

          {/* Show remaining sections only after branch selection */}
          {formData.branch && selectedRecruitment && (
            <>
          {/* Section 1: Basic Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500">
              II. Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name (Chinese / English) *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Student ID *
                </label>
                <input
                  type="text"
                  name="studentId"
                  value={formData.studentId}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Major and Year * (e.g., Economics/2)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    name="major"
                    value={formData.major}
                    onChange={handleChange}
                    placeholder="Economics"
                    required
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    placeholder="2"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Line ID
                </label>
                <input
                  type="text"
                  name="lineId"
                  value={formData.lineId}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instagram ID *
                </label>
                <input
                  type="text"
                  name="instagramId"
                  value={formData.instagramId}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What's your MBTI? *
                </label>
                <input
                  type="text"
                  name="mbti"
                  value={formData.mbti}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </section>

          {/* Section 2: Position Priority */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500">
              III. Position Priority
            </h2>
            <p className="text-gray-600 mb-4">Please mark your 1st, 2nd, and 3rd Priority for each position.</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1st Priority *
                </label>
                <select
                  name="position1stPriority"
                  value={formData.position1stPriority}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  <option value="General Affairs">General Affairs</option>
                  <option value="Public Relation">Public Relation</option>
                  <option value="Event Planner">Event Planner</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2nd Priority
                </label>
                <select
                  name="position2ndPriority"
                  value={formData.position2ndPriority}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  {getAvailableOptions('2nd').map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3rd Priority
                </label>
                <select
                  name="position3rdPriority"
                  value={formData.position3rdPriority}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  {getAvailableOptions('3rd').map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Section 3: Motivation & Talent */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500">
              IV. Motivation & Talent
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  [Motivation] What makes you want to be a staff member? *
                </label>
                <textarea
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  [Development] What would you like to develop as a SLAM staff? *
                </label>
                <textarea
                  name="developmentGoal"
                  value={formData.developmentGoal}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Do you have a portfolio? *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasPortfolio"
                      value="true"
                      checked={formData.hasPortfolio === true}
                      onChange={() => setFormData({ ...formData, hasPortfolio: true })}
                      required
                      className="mr-2"
                    />
                    Yes, I will attach it below
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasPortfolio"
                      value="false"
                      checked={formData.hasPortfolio === false}
                      onChange={() => setFormData({ ...formData, hasPortfolio: false })}
                      required
                      className="mr-2"
                    />
                    No, but I would like to learn as active members
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Put your 'Portfolio' or 'Intro' link here *
                </label>
                <input
                  type="url"
                  name="portfolioLink"
                  value={formData.portfolioLink}
                  onChange={handleChange}
                  required
                  placeholder="https://..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recruitment Bonus: Did you visit us during Club Expo and share on IG Story?
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasRecruitmentBonus"
                      value="true"
                      checked={formData.hasRecruitmentBonus === true}
                      onChange={() => setFormData({ ...formData, hasRecruitmentBonus: true, recruitmentBonusLink: '' })}
                      className="mr-2"
                    />
                    YES, I have shared
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasRecruitmentBonus"
                      value="false"
                      checked={formData.hasRecruitmentBonus === false}
                      onChange={() => setFormData({ ...formData, hasRecruitmentBonus: false, recruitmentBonusLink: '' })}
                      className="mr-2"
                    />
                    NO, I haven't
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Commitment */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500">
              V. Commitment
            </h2>
            
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h3 className="font-semibold mb-2">SLAM Staff Commitments:</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>You must join SLAM Events & Outing</li>
                <li>Meeting (Follow leader's instruction) and get the work done by the deadline</li>
                {formData.branch === 'NCCU' && (
                  <li>500 NTD joining fee (one-time payment) is required to demonstrate your commitment to the team. After payment, you can enjoy all STAFF benefits.</li>
                )}
              </ul>
            </div>

            <label className="flex items-start">
              <input
                type="checkbox"
                name="agreesWithCommitment"
                checked={formData.agreesWithCommitment}
                onChange={handleChange}
                required
                className="mt-1 mr-3"
              />
              <span className="text-gray-700">Do you agree with the SLAM Staff Commitments? *</span>
            </label>
          </section>

          {/* Submit Button */}
          <div className="flex justify-center pt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-4 px-12 rounded-full text-lg hover:from-blue-700 hover:to-indigo-700 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit Application"}
            </button>
          </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

